/**
 * <StudentSettingsPreferencesPage> — « Réglages », la quatrième page des paramètres de l'élève (page).
 *
 * L'onglet d'aujourd'hui, dans son ordre : les notifications (une ligne par type, une bascule par
 * canal), l'aide, le lien discret qui mène à la fermeture du compte, et « À propos ». Seules les
 * notifications s'enregistrent : c'est ce que le bouton du pied envoie. « Heure de rappel » n'est
 * pas portée : aucun type ne l'offre à l'élève. Il n'y a ni apparence ni langue ici.
 *
 * « Noter l'application » n'existe que sur mobile : sans `onRateApp`, pas de ligne.
 */
import React from "react"

import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import {
    NotificationChannelsList,
    type NotificationChannel,
    type UINotificationSetting,
} from "../../molecules/NotificationChannelsList"
import { SettingsSection, SettingsSectionSkeleton, type SettingsSectionRow } from "../../molecules/SettingsSection"
import { AccountSettingsTemplate, type AccountSettingsShellProps } from "../../templates/AccountSettingsTemplate"

export type StudentSettingsPreferencesPageProps = AccountSettingsShellProps & {
    /** `null` tant que la liste n'est pas là. */
    notifications: UINotificationSetting[] | null
    onToggleNotification: (slug: string, channel: NotificationChannel, next: boolean) => void
    onContactSupport: () => void
    /** Le lien vers « Fermer mon compte ». */
    onCloseAccount: () => void
    onOpenPrivacy: () => void
    onOpenLegal: () => void
    /** Mobile seulement. */
    onRateApp?: () => void
}

function PreferencesSkeleton() {
    return (
        <Stack gap="4">
            <SettingsSectionSkeleton title="Notifications" rows={6} withIcon={false} />
            <SettingsSectionSkeleton title="Aide" rows={1} />
            <SettingsSectionSkeleton title="À propos" rows={2} />
        </Stack>
    )
}

export function StudentSettingsPreferencesPage(props: StudentSettingsPreferencesPageProps) {
    const {
        notifications,
        onToggleNotification,
        onContactSupport,
        onCloseAccount,
        onOpenPrivacy,
        onOpenLegal,
        onRateApp,
        ...shell
    } = props

    const aboutRows: SettingsSectionRow[] = [
        { key: "privacy", iconName: "lock", label: "Confidentialité", onPress: onOpenPrivacy },
        { key: "legal", iconName: "file-lines", label: "Mentions légales", onPress: onOpenLegal },
    ]
    if (onRateApp != null) {
        aboutRows.push({ key: "rate", iconName: "star", label: "Noter l'application", onPress: onRateApp })
    }

    return (
        <AccountSettingsTemplate {...shell} title="Réglages" skeleton={<PreferencesSkeleton />}>
            {notifications != null && (
                <Stack gap="4">
                    {notifications.length > 0 && (
                        <SettingsSection title="Notifications">
                            <NotificationChannelsList
                                items={notifications}
                                onToggle={onToggleNotification}
                                disabled={shell.submitting === true}
                            />
                        </SettingsSection>
                    )}

                    <Stack gap="2">
                        <SettingsSection
                            title="Aide"
                            rows={[
                                {
                                    key: "support",
                                    iconName: "headset",
                                    label: "Contacter le support",
                                    onPress: onContactSupport,
                                },
                            ]}
                        />
                        <Row justify="center">
                            <TextButton
                                label="Fermer mon compte"
                                size="sm"
                                color="text-muted"
                                onPress={onCloseAccount}
                            />
                        </Row>
                    </Stack>

                    <SettingsSection title="À propos" rows={aboutRows} />
                </Stack>
            )}
        </AccountSettingsTemplate>
    )
}
